/**
 * The widget's copy and locale, provided to every component below the root.
 *
 * WHY THIS IS IN `vendor/`, now that it names no package. It used to be the one
 * place this app was allowed to name `@axon/i18n`, and the seam outlived the
 * dependency: every component reads its copy through `useText()` and its locale
 * through `useLocale()`, so where the strings come from is decided here and
 * nowhere else. There is no Lingui MACRO here and none in any component:
 * `packages/i18n/lingui.config.ts` does not extract from `apps/widget/src`, so a
 * macro would produce a message id that no catalogue contains — see the header
 * of `src/i18n/messages.ts`. Copy comes from `useText()`, which is typed against
 * the id union and therefore cannot render an id at a visitor by accident.
 *
 * PROVIDE/INJECT RATHER THAN A STORE (ADR-0247). The React version was a context
 * provider; this is the same shape in Vue's vocabulary. The root calls
 * `provideWidgetI18n` with the negotiated locale, every descendant injects. A
 * global store would make the locale reachable from a module that runs before
 * any locale has been negotiated, which is the bug the provider shape prevents.
 *
 * THE TIME ZONE IS NOT HERE, and that is deliberate. The transcript reads its
 * clock from `useVisitorFormat()`, which uses `visitorTimeZone()`: that reports a
 * `source` (`device` vs `fallback`), and `format.ts` prints the offset when the
 * zone is not the visitor's own. Routing the transcript through this file would
 * silently drop that qualification.
 *
 * THE LOCALE IS THE WIDGET'S OWN VOCABULARY of fifty (`src/i18n/locales.ts`),
 * not the console's two — the Lingui runtime was never used here for anything
 * but satisfying a provider, and is gone with it.
 */
import { inject, provide, readonly, shallowRef, watch, type InjectionKey, type Ref } from 'vue';

import {
  WIDGET_FALLBACK_CATALOG,
  loadWidgetCatalog,
  type WidgetCatalog,
  type WidgetMessageId,
} from '../i18n/messages';
import {
  WIDGET_DEFAULT_LOCALE,
  WIDGET_LOCALE_BCP47,
  widgetLocaleDirection,
  type WidgetLocale,
} from '../i18n/locales';

interface WidgetI18n {
  readonly locale: Readonly<Ref<WidgetLocale>>;
  readonly catalog: Readonly<Ref<WidgetCatalog>>;
}

const WIDGET_I18N: InjectionKey<WidgetI18n> = Symbol('omni-widget-i18n');

/**
 * Outside a provider — `UnavailableNotice` mounted from `main.ts` before any
 * brand is known — a component still gets English rather than a crash. The
 * values are constants, so nothing here can change under it.
 */
const FALLBACK_I18N: WidgetI18n = {
  locale: readonly(shallowRef<WidgetLocale>(WIDGET_DEFAULT_LOCALE)),
  catalog: readonly(shallowRef<WidgetCatalog>(WIDGET_FALLBACK_CATALOG)),
};

export function provideWidgetI18n(locale: Readonly<Ref<WidgetLocale>>): void {
  /**
   * ENGLISH UNTIL THE REAL CATALOGUE ARRIVES, never nothing.
   *
   * The fallback is compiled in, so the first paint is always real copy; the
   * swap to the visitor's language is a CORRECTION, not a load. A visitor whose
   * language is English sees one render; everyone else sees English for as long
   * as one same-origin, cache-hit chunk takes.
   *
   * `shallowRef` because a catalogue is replaced whole and never mutated: a deep
   * proxy over two hundred strings would be machinery guarding against an edit
   * that cannot happen.
   */
  const catalog = shallowRef<WidgetCatalog>(WIDGET_FALLBACK_CATALOG);

  watch(
    locale,
    (next, _previous, onCleanup) => {
      if (next === WIDGET_DEFAULT_LOCALE) {
        catalog.value = WIDGET_FALLBACK_CATALOG;
        return;
      }

      // `cancelled` rather than an AbortController: a dynamic import cannot be
      // aborted, and what has to be prevented is not the fetch but a LATE
      // ARRIVAL overwriting a newer choice. A visitor who clicks through three
      // languages while the first chunk is still in flight must end on the third.
      let cancelled = false;
      onCleanup(() => {
        cancelled = true;
      });
      void loadWidgetCatalog(next)
        .then((loaded) => {
          if (!cancelled) catalog.value = loaded;
        })
        .catch(() => {
          // A chunk that fails to load is a network fault on somebody else's
          // page, not something a visitor can act on. English is already
          // rendered and stays rendered; saying "could not load your language"
          // in a language they may not read would be worse than the silence.
          if (!cancelled) catalog.value = WIDGET_FALLBACK_CATALOG;
        });
    },
    { immediate: true },
  );

  /**
   * `lang` and `dir` ON THE DOCUMENT, because this document IS the widget.
   *
   * The panel lives in its own iframe, so `<html>` belongs to us entirely and
   * setting it there is what makes every descendant inherit — including the
   * composer's typing direction, which a wrapper element around the component
   * tree would not reliably reach.
   *
   * FOUR OF THE FIFTY READ RIGHT TO LEFT, and without this they would get
   * correct Arabic, Persian, Hebrew or Urdu laid out backwards. `lang` is the
   * other half and is not decoration: a screen reader picks its voice from it.
   */
  watch(
    locale,
    (next) => {
      const root = document.documentElement;
      root.lang = WIDGET_LOCALE_BCP47[next];
      root.dir = widgetLocaleDirection(next);
    },
    { immediate: true },
  );

  provide(WIDGET_I18N, { locale, catalog: readonly(catalog) });
}

/** The locale the panel is currently rendering in. */
export function useLocale(): Readonly<Ref<WidgetLocale>> {
  return inject(WIDGET_I18N, FALLBACK_I18N).locale;
}

/**
 * Copy, typed against the id union so a mistyped id is a compile error rather
 * than an id rendered at a visitor.
 *
 * The returned function reads the catalogue AT CALL TIME, which is what makes it
 * reactive: called from a template or a `computed`, it registers the catalogue
 * as a dependency, so the English first frame re-renders in the visitor's
 * language the moment the chunk lands. Every id exists in every catalogue,
 * which `catalogs.test.ts` asserts for all fifty.
 */
export function useText(): (id: WidgetMessageId) => string {
  const { catalog } = inject(WIDGET_I18N, FALLBACK_I18N);
  return (id) => catalog.value[id];
}

export type { WidgetLocale };
